import clsx from 'clsx';
import type { ReactNode, ElementType } from 'react';

type DisplayVariant = 'h1' | 'subhead';

type BaseProps = {
  variant: DisplayVariant;
  className?: string;
  children: ReactNode;
  as?: keyof JSX.IntrinsicElements;
};

type PolymorphicProps<TTag extends keyof JSX.IntrinsicElements> = BaseProps &
  Omit<JSX.IntrinsicElements[TTag], keyof BaseProps>;

function resolveDefaultTag(variant: DisplayVariant) {
  if (variant === 'h1') {
    return 'h1';
  }
  return 'p';
}

const variantClasses: Record<DisplayVariant, string> = {
  h1: 'text-5xl md:text-6xl lg:text-7xl font-bold leading-tight whitespace-pre-line',
  subhead: 'text-lg md:text-xl text-white/70 leading-relaxed whitespace-pre-line',
};

export default function Display<TTag extends keyof JSX.IntrinsicElements = 'p'>(
  props: PolymorphicProps<TTag>,
) {
  const { variant, className, as, children, ...rest } = props;
  const Component = (as ?? resolveDefaultTag(variant)) as ElementType;

  return (
    <Component className={clsx(variantClasses[variant], className)} {...rest}>
      {children}
    </Component>
  );
}

export { Display };
